import { useEffect, useMemo, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries, LineStyle } from "lightweight-charts";

/**
 * ChartViewer — thin wrapper around lightweight-charts v5.
 * Renders candles + volume histogram for a given (exchange, market_type, symbol, timeframe).
 *
 * `plan` (optional) draws the active V2 trade plan as horizontal price
 * lines on the candle series: Entry zone (upper + lower), Stop-loss,
 * and TP1/TP2/TP3. All values come from the live analysis — nothing is
 * hardcoded. Pass `null` or omit to render chart only (viewer stage).
 */
export default function ChartViewer({ candles = [], loading = false, error = null, height = 520, plan = null }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const planLinesRef = useRef([]);

  // --- mount chart once ---
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      layout: {
        background: { color: "#0a0a0a" },
        textColor: "#a0a0a0",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
      grid: {
        vertLines: { color: "#111114" },
        horzLines: { color: "#111114" },
      },
      rightPriceScale: { borderColor: "#1a1a1e" },
      timeScale: { borderColor: "#1a1a1e", timeVisible: true, secondsVisible: false },
      crosshair: { mode: 0 },
    });
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#00FF66",
      downColor: "#FF4F4F",
      borderUpColor: "#00FF66",
      borderDownColor: "#FF4F4F",
      wickUpColor: "#00FF66",
      wickDownColor: "#FF4F4F",
    });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        chart.applyOptions({ width: e.contentRect.width });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [height]);

  // --- feed data whenever candles change ---
  useEffect(() => {
    const cs = candleSeriesRef.current;
    const vs = volumeSeriesRef.current;
    if (!cs || !vs) return;
    if (!candles || candles.length === 0) {
      cs.setData([]);
      vs.setData([]);
      return;
    }
    // lightweight-charts expects seconds for the `time` field
    const bars = candles.map((k) => ({
      time: Math.floor(k.t / 1000),
      open: k.o,
      high: k.h,
      low: k.l,
      close: k.c,
    }));
    const vols = candles.map((k) => ({
      time: Math.floor(k.t / 1000),
      value: k.v,
      color: k.c >= k.o ? "rgba(0,255,102,0.35)" : "rgba(255,79,79,0.35)",
    }));
    cs.setData(bars);
    vs.setData(vols);
    chartRef.current?.timeScale().fitContent();
  }, [candles]);

  // --- plan overlay: redraw horizontal price lines when `plan` changes ---
  useEffect(() => {
    const cs = candleSeriesRef.current;
    if (!cs) return;
    // Clear previous lines
    for (const line of planLinesRef.current) {
      try { cs.removePriceLine(line); } catch { /* ignore */ }
    }
    planLinesRef.current = [];
    if (!plan) return;
    // Never draw a long plan whose stop is not below the entry.
    if (plan.entryLow == null || plan.stop == null || plan.stop >= plan.entryLow) return;

    const addLine = (price, title, color, style = LineStyle.Solid, width = 2) => {
      if (price == null || Number.isNaN(price)) return;
      const line = cs.createPriceLine({
        price,
        color,
        lineStyle: style,
        lineWidth: width,
        axisLabelVisible: true,
        title,
      });
      planLinesRef.current.push(line);
    };

    // Entry — V2 single-entry contract: ALWAYS one solid cyan line
    // at the low-entry price. Any "high entry" (upper range bound)
    // is forbidden and must never be drawn, even if the backend
    // slipped a legacy range through.
    if (plan.entryLow != null) {
      addLine(plan.entryLow, "Entry", "#00E5FF", LineStyle.Solid, 2);
    }
    // Stop — red solid
    addLine(plan.stop, "Stop", "#FF4757", LineStyle.Solid, 2);
    // Targets — green, lighter width for lower rungs
    if (Array.isArray(plan.targets) && plan.targets.length) {
      plan.targets.forEach((t, i) => {
        addLine(t.price, `TP${i + 1}`, "#00FF66", LineStyle.Solid, i === plan.targets.length - 1 ? 2 : 1);
      });
    } else if (plan.target != null) {
      addLine(plan.target, "Target", "#00FF66", LineStyle.Solid, 2);
    }
  }, [plan]);

  return (
    <div className="relative" data-testid="chart-viewer">
      <div ref={containerRef} className="w-full" style={{ height }} />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm text-xs font-mono uppercase tracking-[0.3em] text-neutral-300">
          loading…
        </div>
      )}
      {error && !loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-sm text-red-400 text-center px-6" data-testid="chart-viewer-error">
          {error}
        </div>
      )}
    </div>
  );
}
